import React, { useState, useEffect } from 'react';
import Header from '../../components/navigation/Header';
import GlobalFilterBar from '../../components/navigation/GlobalFilterBar';
import ComplianceAlertBanner from '../../components/navigation/ComplianceAlertBanner';
import DataRefreshIndicator from '../../components/navigation/DataRefreshIndicator';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import clientCustomizationService from '../../services/clientCustomizationService';
import RequestPipelineCard from './components/RequestPipelineCard';
import RequestWorkflowTable from './components/RequestWorkflowTable';
import RequestDetailsModal from './components/RequestDetailsModal';

const ClientCustomizationRequestManager = () => {
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [globalFilter, setGlobalFilter] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [filters, setFilters] = useState({
    status: 'all',
    priority: 'all',
    type: 'all',
    complexity: 'all',
    searchTerm: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [requests, filters, activeTab]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const [requestsData, statsData] = await Promise.all([
        clientCustomizationService?.getAllRequests(),
        clientCustomizationService?.getCustomizationStatistics()
      ]);

      setRequests(requestsData);
      setStatistics(statsData);
    } catch (err) {
      setError(err?.message || 'Failed to load customization requests');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...requests];

    // Tab filter
    if (activeTab !== 'all') {
      if (activeTab === 'active') {
        filtered = filtered?.filter(r => 
          ['submitted', 'under_review', 'approved', 'in_development', 'testing']?.includes(r?.requestStatus)
        );
      } else if (activeTab === 'completed') {
        filtered = filtered?.filter(r => r?.requestStatus === 'completed');
      } else if (activeTab === 'pending') {
        filtered = filtered?.filter(r => ['draft', 'submitted', 'under_review']?.includes(r?.requestStatus));
      }
    }

    // Search filter
    if (filters?.searchTerm) {
      const term = filters?.searchTerm?.toLowerCase();
      filtered = filtered?.filter(request =>
        request?.requestTitle?.toLowerCase()?.includes(term) ||
        request?.requestNumber?.toLowerCase()?.includes(term) ||
        request?.companyName?.toLowerCase()?.includes(term) ||
        request?.assignedDeveloper?.toLowerCase()?.includes(term)
      );
    }

    // Status filter
    if (filters?.status !== 'all') {
      filtered = filtered?.filter(request => request?.requestStatus === filters?.status);
    }

    // Priority filter
    if (filters?.priority !== 'all') {
      filtered = filtered?.filter(request => request?.priority === filters?.priority);
    }

    // Type filter
    if (filters?.type !== 'all') {
      filtered = filtered?.filter(request => request?.customizationType === filters?.type);
    }

    // Complexity filter
    if (filters?.complexity !== 'all') {
      filtered = filtered?.filter(request => request?.customizationComplexity === filters?.complexity);
    }

    setFilteredRequests(filtered);
  };

  const handleViewDetails = (request) => {
    setSelectedRequest(request);
    setShowDetailsModal(true);
  };

  const handleCloseModal = () => {
    setShowDetailsModal(false);
    setSelectedRequest(null);
  };

  const handleStatusUpdate = async (requestId, newStatus) => {
    try {
      await clientCustomizationService?.updateRequestStatus(requestId, newStatus, 'admin@system.com');
      await loadData();
    } catch (err) {
      setError(err?.message || 'Failed to update request status');
    }
  };

  const handleExport = () => {
    try {
      const headers = [
        'Request Number',
        'Company',
        'Title',
        'Type',
        'Status',
        'Priority',
        'Complexity',
        'Estimated Cost (K)',
        'Progress (%)',
        'Assigned Developer',
        'Target Completion'
      ];

      const rows = filteredRequests?.map(request => [
        request?.requestNumber || '',
        request?.companyName || '',
        request?.requestTitle || '',
        request?.customizationType || '',
        request?.requestStatus || '',
        request?.priority || '',
        request?.customizationComplexity || '',
        request?.estimatedCostKina || 0,
        request?.developmentProgressPercentage || 0,
        request?.assignedDeveloper || 'Unassigned',
        request?.targetCompletionDate || 'N/A'
      ]);

      const csvContent = [
        headers?.join(','),
        ...rows?.map(row => row?.map(cell => `"${cell}"`)?.join(','))
      ]?.join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link?.setAttribute('href', url);
      link?.setAttribute('download', `customization-requests-${new Date()?.toISOString()?.split('T')?.[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body?.appendChild(link);
      link?.click();
      document.body?.removeChild(link);
    } catch (err) {
      setError('Failed to export data');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <GlobalFilterBar onFilterChange={setGlobalFilter} />
        <ComplianceAlertBanner />
        <DataRefreshIndicator />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <Icon name="Loader2" className="animate-spin h-8 w-8 mx-auto mb-4 text-primary" />
            <p className="text-muted-foreground">Loading customization requests...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <GlobalFilterBar onFilterChange={setGlobalFilter} />
      <ComplianceAlertBanner />
      <DataRefreshIndicator />
      <main className="container mx-auto px-6 py-8 mt-16">
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Client Customization Request Manager</h1>
              <p className="text-muted-foreground">Comprehensive workflow management for client-specific feature requests and custom integrations</p>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={handleExport}
              >
                <Icon name="Download" size={16} className="mr-2" />
                Export Report
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={loadData}
              >
                <Icon name="RefreshCw" size={16} className="mr-2" />
                Refresh
              </Button>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-error/10 border border-error text-error px-4 py-3 rounded-md mb-6">
            <div className="flex items-center gap-2">
              <Icon name="AlertCircle" size={18} />
              <span>{error}</span>
            </div>
          </div>
        )}

        {/* Request Pipeline Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <RequestPipelineCard
            title="Total Requests"
            value={statistics?.totalRequests || 0}
            icon="FileText"
            color="blue"
            subtitle=""
          />
          <RequestPipelineCard
            title="Active Requests"
            value={statistics?.activeRequests || 0}
            icon="Activity"
            color="amber"
            subtitle=""
          />
          <RequestPipelineCard
            title="Completed"
            value={statistics?.completedRequests || 0}
            icon="CheckCircle"
            color="green"
            subtitle=""
          />
          <RequestPipelineCard
            title="Total Revenue"
            value={`K${(statistics?.totalRevenue || 0)?.toLocaleString()}`}
            subtitle={`Pending: K${(statistics?.pendingRevenue || 0)?.toLocaleString()}`}
            icon="DollarSign"
            color="emerald"
          />
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <div className="border-b border-border">
            <div className="flex gap-6">
              {[
                { key: 'all', label: 'All Requests', count: requests?.length },
                { key: 'active', label: 'Active', count: requests?.filter(r => ['submitted', 'under_review', 'approved', 'in_development', 'testing']?.includes(r?.requestStatus))?.length },
                { key: 'pending', label: 'Pending Review', count: requests?.filter(r => ['draft', 'submitted', 'under_review']?.includes(r?.requestStatus))?.length },
                { key: 'completed', label: 'Completed', count: requests?.filter(r => r?.requestStatus === 'completed')?.length }
              ]?.map(tab => (
                <button
                  key={tab?.key}
                  onClick={() => setActiveTab(tab?.key)}
                  className={`py-3 px-4 border-b-2 transition-colors ${
                    activeTab === tab?.key
                      ? 'border-primary text-primary font-medium' : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {tab?.label}
                  <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-muted">
                    {tab?.count}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-card border border-border rounded-lg p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Search</label>
              <input
                type="text"
                value={filters?.searchTerm}
                onChange={(e) => setFilters({ ...filters, searchTerm: e?.target?.value })}
                placeholder="Search requests..."
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Status</label>
              <select
                value={filters?.status}
                onChange={(e) => setFilters({ ...filters, status: e?.target?.value })}
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
              >
                <option value="all">All Statuses</option>
                <option value="draft">Draft</option>
                <option value="submitted">Submitted</option>
                <option value="under_review">Under Review</option>
                <option value="approved">Approved</option>
                <option value="in_development">In Development</option>
                <option value="testing">Testing</option>
                <option value="deployed">Deployed</option>
                <option value="completed">Completed</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Priority</label>
              <select
                value={filters?.priority}
                onChange={(e) => setFilters({ ...filters, priority: e?.target?.value })}
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
              >
                <option value="all">All Priorities</option>
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Type</label>
              <select
                value={filters?.type}
                onChange={(e) => setFilters({ ...filters, type: e?.target?.value })}
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
              >
                <option value="all">All Types</option>
                <option value="dashboard_modification">Dashboard Modification</option>
                <option value="integration_requirement">Integration Requirement</option>
                <option value="compliance_adjustment">Compliance Adjustment</option>
                <option value="custom_report">Custom Report</option>
                <option value="workflow_automation">Workflow Automation</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Complexity</label>
              <select
                value={filters?.complexity}
                onChange={(e) => setFilters({ ...filters, complexity: e?.target?.value })}
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
              >
                <option value="all">All Complexities</option>
                <option value="simple">Simple</option>
                <option value="moderate">Moderate</option>
                <option value="complex">Complex</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>
          </div>
        </div>

        {/* Request Workflow Table */}
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <RequestWorkflowTable
            requests={filteredRequests}
            onViewDetails={handleViewDetails}
            onStatusUpdate={handleStatusUpdate}
          />
        </div>
      </main>
      {/* Request Details Modal */}
      {showDetailsModal && selectedRequest && (
        <RequestDetailsModal
          request={selectedRequest}
          onClose={handleCloseModal}
          onStatusUpdate={handleStatusUpdate}
          onRefresh={loadData}
        />
      )}
    </div>
  );
};

export default ClientCustomizationRequestManager;